// src/pages/MessagingAdmin.jsx
// メッセージ機能の管理（運営者＝開発者モードの開発者だけ）（/messages/admin）【2026-10-16】
//   ・通報を見て、対応を決める（問題なし／注意／利用停止）。利用停止の解除
//   ・機能スイッチ（機能ごとに開ける・閉じる）と、保存場所の空き
//   ・中身を見られるのは「通報されたやり取り」だけ。見たこと・操作したことはサーバーに記録される
//   API: GET /admin/reports, GET /admin/reports/:id, POST /admin/reports/:id/resolve,
//        DELETE /admin/suspensions/:userId, GET /admin/features, PUT /admin/features/:feature
//   開発者モードがオフのときは、サーバーが 403（DEVELOPER_MODE_OFF / FORBIDDEN）を返すので、その文を出す
import React, { useCallback, useEffect, useState } from 'react';
import { msgApi, loadFeatures } from '../messaging/api';
import { Page, Notice, Loading, Empty, Tag, AuthImage } from '../messaging/admin/ui';
import { fmtDateTime, fmtBytes, errText } from '../messaging/admin/util';

const FEATURE_LABEL = {
  teacher_dm: '先生への個別メッセージ',
  announcements: '授業のお知らせ',
  student_chat: '学生どうし',
  renewal: '1年延長の確認',
  proximity: '近づけて追加',
  class_chat: '授業グループ',
  notes: 'ノート',
  bulletins: '大学からの一斉連絡',
};
const FEATURE_DESC = {
  teacher_dm: '学生が授業の先生に個別にメッセージを送れます。',
  announcements: '先生が授業の履修者全員にお知らせを送れます。',
  student_chat: '学生どうしの個別・グループのやり取り。開ける前に利用規約・プライバシーポリシーを確かめてください。',
  renewal: '学生どうしの会話を1年ごとに続けるか確かめます。',
  proximity: 'スマホを近づけて相手を追加します（学生どうしが開いているときだけ）。',
  class_chat: '授業の履修者だけのグループ（先生は入りません）。',
  notes: '会話の中にノートを作れます。',
  bulletins: '教務が学部・学年を選んで学生に一斉連絡できます。',
};
// 開けるときに規約の確認がいるもの
const NEEDS_POLICY = ['student_chat', 'class_chat'];

const KIND_LABEL = {
  teacher_dm: '先生への個別',
  direct: '学生どうし（個別）',
  group: '学生グループ',
  class_group: '授業グループ',
};
const ACTION_LABEL = { none: '問題なし', warn: '注意', suspend: '利用停止' };

function reportTarget(r) {
  if (r.request_id) return '申請の一言';
  if (r.note_comment_id) return 'ノートのコメント';
  if (r.note_id) return 'ノート';
  if (r.message_id) return 'メッセージ';
  return '—';
}

// ── 通報の一覧 ─────────────────────────────────────────────
function ReportList({ status, onOpen, onDenied }) {
  const [list, setList] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setList(null);
    const r = await msgApi.get('/admin/reports?status=' + status);
    if (!r.success) {
      setErr(errText(r)); setList([]);
      if (r.status === 403) onDenied(errText(r));
      return;
    }
    setErr('');
    setList(r.reports || []);
  }, [status, onDenied]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>{status === 'open' ? 'まだ対応していない通報' : '対応した通報'}</h2>
        <button type="button" className="btn-secondary ma-btn-sm" onClick={load}>最新にする</button>
      </div>
      {err && <Notice kind="error">{err}</Notice>}
      {list === null ? <Loading /> : list.length === 0 ? (
        !err && <Empty>{status === 'open' ? 'まだ対応していない通報はありません。' : '対応した通報はまだありません。'}</Empty>
      ) : (
        <div className="ma-list">
          {list.map(r => (
            <button type="button" className="ma-report-row" key={r.id} onClick={() => onOpen(r)}>
              <div className="ma-item-head" style={{ marginBottom: '0.2rem' }}>
                <span>#{r.id}</span>
                <span>{fmtDateTime(r.created_at)}</span>
                {r.conversation_kind && <Tag tone="gray">{KIND_LABEL[r.conversation_kind] || r.conversation_kind}</Tag>}
                {r.reports_against_user > 1 && <Tag tone="amber">この人への通報 {r.reports_against_user}件</Tag>}
                {r.action && <Tag tone={r.action === 'suspend' ? 'red' : r.action === 'warn' ? 'amber' : 'green'}>{ACTION_LABEL[r.action] || r.action}</Tag>}
              </div>
              <div className="ma-report-who">
                {r.reporter_name || '（退会した人）'} → {r.reported_name || '（相手不明）'}
              </div>
              <div className="ma-report-reason">{r.reason || '（理由の記入なし）'}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 通報の中身 ─────────────────────────────────────────────
function ContextMessage({ m }) {
  const cls = 'ma-msg'
    + (m.reported ? ' ma-msg--reported' : '')
    + (m.kind === 'system' ? ' ma-msg--system' : '')
    + (m.retracted_at ? ' ma-msg--retracted' : '');
  const prev = m.previous_versions || [];
  return (
    <div className={cls}>
      <div className="ma-msg-head">
        <span className="ma-msg-name">{m.sender ? m.sender.name : m.kind === 'system' ? 'お知らせ' : '—'}</span>
        <span>{fmtDateTime(m.created_at)}</span>
        {m.reported && <Tag tone="red">通報されたメッセージ</Tag>}
        {m.retracted_at && <Tag tone="gray">取り消し済み（{fmtDateTime(m.retracted_at)}）</Tag>}
        {m.edited_at && <Tag tone="gray">編集済み</Tag>}
      </div>
      {m.text && <div className="ma-msg-text">{m.text}</div>}
      {m.image && (
        <div className="ma-attach"><AuthImage url={'/api/messages/admin/images/' + m.image.id} alt="添付の画像" /></div>
      )}
      {prev.length > 0 && (
        <details className="ma-prev">
          <summary>直す前の本文（{prev.length}件）</summary>
          {prev.map((p, i) => (
            <div className="ma-prev-item" key={i}>
              <div className="ma-muted" style={{ fontSize: '0.74rem' }}>{fmtDateTime(p.replaced_at)} に直す前</div>
              {p.text || '（本文なし）'}
            </div>
          ))}
        </details>
      )}
    </div>
  );
}

function ResolveForm({ reportId, onDone }) {
  const [action, setAction] = useState('none');
  const [days, setDays] = useState(7);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setErr('');
    const d = Math.min(365, Math.max(1, parseInt(days, 10) || 7));
    const q = action === 'suspend'
      ? `通報された人のメッセージ機能を ${d}日間止めます。本人に通知されます。よろしいですか？`
      : action === 'warn'
        ? '通報された人に注意の通知を送ります。よろしいですか？'
        : '問題なしとして閉じます。よろしいですか？';
    if (!window.confirm(q + '\n（同じメッセージへのほかの通報もまとめて閉じます）')) return;
    setBusy(true);
    const body = { action, note: note.trim() };
    if (action === 'suspend') body.days = d;
    const r = await msgApi.post('/admin/reports/' + reportId + '/resolve', body);
    setBusy(false);
    if (!r.success) { setErr(errText(r)); return; }
    onDone();
  };

  const choices = [
    { key: 'none', t: '問題なし', d: '何もせずに閉じます。本人には知らせません。' },
    { key: 'warn', t: '注意する', d: '「使い方に気をつけてください」と本人に通知します。' },
    { key: 'suspend', t: 'メッセージ機能を止める', d: '決めた日数のあいだ、本人はメッセージを送れなくなります（出席には影響しません）。本人に通知します。' },
  ];
  return (
    <div className="card">
      <div className="card-header"><h2 style={{ margin: 0 }}>対応を決める</h2></div>
      <div className="ma-choices" role="radiogroup" aria-label="対応">
        {choices.map(c => (
          <label key={c.key} className={'ma-choice' + (action === c.key ? ' ma-choice--on' : '')}>
            <input type="radio" name="resolve_action" checked={action === c.key} onChange={() => setAction(c.key)} />
            <div>
              <div className="ma-choice-t">{c.t}</div>
              <div className="ma-choice-d">{c.d}</div>
            </div>
          </label>
        ))}
      </div>
      {action === 'suspend' && (
        <div className="ma-field">
          <div className="ma-field-label"><span>止める日数（1〜365日）</span></div>
          <input type="number" className="ma-input ma-input--num" min={1} max={365} value={days}
            onChange={e => setDays(e.target.value)} /> 日間
        </div>
      )}
      <div className="ma-field">
        <div className="ma-field-label"><span>メモ（任意・運営者だけが見ます）</span><span className="ma-count">{note.length} / 500</span></div>
        <textarea className="ma-textarea" style={{ minHeight: '4rem' }} maxLength={500} value={note} onChange={e => setNote(e.target.value)} />
      </div>
      {err && <Notice kind="error">{err}</Notice>}
      <div className="ma-actions ma-actions--end">
        <button type="button" className={action === 'none' ? 'btn-primary' : 'btn-danger'} onClick={submit} disabled={busy}>
          {busy ? '処理中...' : action === 'none' ? '問題なしで閉じる' : action === 'warn' ? '注意して閉じる' : '止めて閉じる'}
        </button>
      </div>
    </div>
  );
}

function ReportDetail({ summary, onBack }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await msgApi.get('/admin/reports/' + summary.id);
    if (!r.success) { setErr(errText(r)); return; }
    setErr('');
    setData(r);
  }, [summary.id]);
  useEffect(() => { load(); }, [load]);

  const unsuspend = async (userId) => {
    if (!window.confirm('この人の利用停止を解除しますか？')) return;
    setBusy(true);
    const r = await msgApi.del('/admin/suspensions/' + userId);
    setBusy(false);
    if (!r.success) { setMsg({ kind: 'error', text: errText(r) }); return; }
    setMsg({ kind: 'ok', text: '利用停止を解除しました' });
    load();
  };

  const rp = data && data.report;
  const susp = data && data.suspension;
  const suspActive = susp && new Date(susp.until).getTime() > Date.now();

  return (
    <>
      <div className="ma-actions" style={{ marginBottom: '1rem' }}>
        <button type="button" className="btn-secondary" onClick={() => onBack(false)}>← 一覧に戻る</button>
      </div>
      {err && <Notice kind="error">{err}</Notice>}
      {msg && <Notice kind={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Notice>}
      {!data && !err && <div className="card"><Loading /></div>}
      {rp && (
        <>
          <div className="card">
            <div className="card-header"><h2 style={{ margin: 0 }}>通報 #{rp.id}</h2></div>
            <dl className="ma-kv">
              <dt>通報された日時</dt><dd>{fmtDateTime(rp.created_at)}</dd>
              <dt>通報した人</dt><dd>{summary.reporter_name || '—'}{rp.reporter_id ? `（ID ${rp.reporter_id}）` : ''}</dd>
              <dt>通報された人</dt>
              <dd>
                {summary.reported_name || '—'}{rp.reported_user_id ? `（ID ${rp.reported_user_id}）` : ''}
                {summary.reports_against_user > 0 && `　これまでの通報 ${summary.reports_against_user}件`}
              </dd>
              <dt>対象</dt>
              <dd>
                {reportTarget(rp)}
                {summary.conversation_kind ? `（${KIND_LABEL[summary.conversation_kind] || summary.conversation_kind}）` : ''}
              </dd>
              <dt>理由</dt><dd>{rp.reason || '（記入なし）'}</dd>
              <dt>状態</dt>
              <dd>
                {rp.status === 'open' ? <Tag tone="amber">未対応</Tag> : <Tag tone="green">対応済み</Tag>}
                {rp.action && <>{' '}{ACTION_LABEL[rp.action] || rp.action}</>}
                {rp.reviewed_at && `（${fmtDateTime(rp.reviewed_at)}）`}
                {rp.review_note && `\nメモ：${rp.review_note}`}
              </dd>
            </dl>
            {rp.reported_user_id && (
              <div style={{ marginTop: '0.9rem' }}>
                {suspActive ? (
                  <Notice kind="warn">
                    この人はいま、メッセージ機能を止められています（{fmtDateTime(susp.until)} まで）。
                    {susp.reason ? `\n理由：${susp.reason}` : ''}
                    <div style={{ marginTop: '0.5rem' }}>
                      <button type="button" className="btn-secondary ma-btn-sm" disabled={busy} onClick={() => unsuspend(rp.reported_user_id)}>
                        {busy ? '解除中...' : '利用停止を解除する'}
                      </button>
                    </div>
                  </Notice>
                ) : (
                  <div className="ma-muted" style={{ fontSize: '0.84rem' }}>この人はいま、利用停止になっていません。</div>
                )}
              </div>
            )}
          </div>

          {rp.request_note != null && (
            <div className="card">
              <div className="card-header"><h2 style={{ margin: 0 }}>申請に付いていた一言</h2></div>
              <div className="ma-msg ma-msg--reported"><div className="ma-msg-text">{rp.request_note || '（一言なし）'}</div></div>
            </div>
          )}
          {rp.request_id && rp.request_note == null && (
            <div className="card"><Notice kind="info">申請の一言は残っていません（すでに消えています）。</Notice></div>
          )}

          {data.note && (
            <div className="card">
              <div className="card-header"><h2 style={{ margin: 0 }}>{data.note.comment ? '通報されたノートのコメント' : '通報されたノート'}</h2></div>
              <div className={'ma-msg' + (data.note.comment ? '' : ' ma-msg--reported')}>
                <div className="ma-msg-head">
                  <span className="ma-msg-name">{data.note.title || '（題名なし）'}</span>
                  <span>{fmtDateTime(data.note.created_at)}</span>
                  {data.note.edited_at && <Tag tone="gray">編集済み</Tag>}
                  {data.note.deleted_at && <Tag tone="gray">削除済み（{fmtDateTime(data.note.deleted_at)}）</Tag>}
                </div>
                <div className="ma-msg-text">{data.note.text || '（本文なし）'}</div>
              </div>
              {data.note.comment && (
                <div className="ma-msg ma-msg--reported" style={{ marginTop: '0.5rem' }}>
                  <div className="ma-msg-head">
                    <span className="ma-msg-name">コメント</span>
                    <span>{fmtDateTime(data.note.comment.created_at)}</span>
                    <Tag tone="red">通報されたコメント</Tag>
                  </div>
                  <div className="ma-msg-text">{data.note.comment.text || '（本文なし）'}</div>
                </div>
              )}
            </div>
          )}

          {(data.context || []).length > 0 && (
            <div className="card">
              <div className="card-header">
                <h2 style={{ margin: 0 }}>前後のやり取り</h2>
                <div className="ma-muted" style={{ marginTop: '0.25rem', fontSize: '0.82rem' }}>
                  通報した人に見えていた範囲だけを出しています。取り消し・編集の前の本文も記録として出します。
                </div>
              </div>
              <div className="ma-thread">
                {data.context.map(m => <ContextMessage key={m.id} m={m} />)}
              </div>
            </div>
          )}

          {rp.status === 'open' && <ResolveForm reportId={rp.id} onDone={() => onBack(true)} />}
        </>
      )}
    </>
  );
}

// ── 機能スイッチ ───────────────────────────────────────────
function Meter({ label, used, total, warnAt, dangerAt, note }) {
  const pct = total ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const tone = dangerAt != null && pct >= dangerAt ? ' ma-meter-fill--danger' : warnAt != null && pct >= warnAt ? ' ma-meter-fill--warn' : '';
  return (
    <div className="ma-meter">
      <div className="ma-meter-top">
        <span style={{ fontWeight: 700 }}>{label}</span>
        <span>{fmtBytes(used)} / {fmtBytes(total)}（{pct}%）</span>
      </div>
      <div className="ma-meter-track"><div className={'ma-meter-fill' + tone} style={{ width: pct + '%' }} /></div>
      {note && <div className="ma-hint">{note}</div>}
    </div>
  );
}

function StorageCard({ s }) {
  if (!s) return null;
  const lim = s.limits || {};
  const free = s.disk_free_bytes;
  const total = s.disk_total_bytes;
  const nearImages = free != null && lim.min_free_for_images_bytes && free < lim.min_free_for_images_bytes * 1.5;
  const imgPct = lim.image_quota_bytes ? (s.images_used_bytes || 0) / lim.image_quota_bytes : 0;
  return (
    <div className="card">
      <div className="card-header"><h2 style={{ margin: 0 }}>保存場所</h2></div>
      {s.writes_blocked && (
        <Notice kind="error">
          ディスクの空きが {fmtBytes(lim.min_free_for_writes_bytes)} を下回ったため、メッセージの送信を止めています（出席の記録を守るため）。
          古いデータを消すか、ディスクを増やしてください。
        </Notice>
      )}
      {!s.writes_blocked && s.images_blocked && (
        <Notice kind="error">
          画像・PDF の受け付けを止めています（文字は送れます）。ディスクの空きか、画像の保存容量の上限に達しています。
        </Notice>
      )}
      {!s.images_blocked && !s.writes_blocked && (nearImages || imgPct >= 0.8) && (
        <Notice kind="warn">
          {nearImages
            ? `ディスクの空きが少なくなっています。空きが ${fmtBytes(lim.min_free_for_images_bytes)} を下回ると、画像の受け付けを止めます。`
            : '画像の保存容量の8割を超えました。上限に達すると、画像の受け付けを止めます。'}
        </Notice>
      )}
      {free == null ? (
        <div className="ma-muted">ディスクの空きを測れませんでした。</div>
      ) : total ? (
        <Meter label="ディスク（使っている量）" used={total - free} total={total} warnAt={80} dangerAt={90}
          note={`空き ${fmtBytes(free)}。空きが ${fmtBytes(lim.min_free_for_images_bytes)} を下回ると画像を、${fmtBytes(lim.min_free_for_writes_bytes)} を下回ると送信をすべて止めます。`} />
      ) : (
        <div style={{ fontSize: '0.9rem', marginBottom: '0.8rem' }}>ディスクの空き：<strong>{fmtBytes(free)}</strong></div>
      )}
      <Meter label="メッセージの画像・PDF" used={s.images_used_bytes || 0} total={lim.image_quota_bytes} warnAt={80} dangerAt={95}
        note={`1人あたりの上限 ${fmtBytes(lim.user_image_quota_bytes)}`} />
    </div>
  );
}

function FeaturesTab({ onDenied }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    const r = await msgApi.get('/admin/features');
    if (!r.success) {
      setErr(errText(r));
      if (r.status === 403) onDenied(errText(r));
      return;
    }
    setErr('');
    setData(r);
  }, [onDenied]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (f) => {
    const next = !f.enabled;
    const name = FEATURE_LABEL[f.feature] || f.feature;
    if (next && NEEDS_POLICY.includes(f.feature)) {
      if (!window.confirm(`「${name}」を開けます。\n\n利用規約・プライバシーポリシーの改定は済んでいますか？\n（「通報されたメッセージは運営者が確認する」ことが書かれている必要があります）`)) return;
    } else if (!window.confirm(next ? `「${name}」を開けますか？` : `「${name}」を閉じますか？\n使っている人の画面から、この機能のボタンが消えます。`)) {
      return;
    }
    setBusy(f.feature); setMsg(null);
    const r = await msgApi.put('/admin/features/' + f.feature, { enabled: next });
    setBusy('');
    if (!r.success) { setMsg({ kind: 'error', text: errText(r) }); return; }
    setMsg(r.warning ? { kind: 'warn', text: r.warning } : { kind: 'ok', text: `「${name}」を${next ? '開けました' : '閉じました'}` });
    loadFeatures(true);
    load();
  };

  if (err) return <div className="card"><Notice kind="error">{err}</Notice></div>;
  if (!data) return <div className="card"><Loading /></div>;
  return (
    <>
      {data.killed && (
        <Notice kind="error">
          サーバーの設定（MESSAGING_DISABLED=1）で、メッセージ機能全体を止めています。ここのスイッチよりそちらが優先されます。
        </Notice>
      )}
      <div className="card">
        <div className="card-header">
          <h2 style={{ margin: 0 }}>機能スイッチ</h2>
          <div className="ma-muted" style={{ marginTop: '0.25rem', fontSize: '0.82rem' }}>
            閉じた機能は、学生・先生の画面からボタンが消えます。開発者には、スイッチに関係なくすべての機能が見えます。
          </div>
        </div>
        {msg && <Notice kind={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Notice>}
        {(data.features || []).length === 0 ? <Empty>スイッチがありません。</Empty> : (
          <div>
            {data.features.map(f => (
              <div className="ma-switch-row" key={f.feature}>
                <div className="ma-switch-info">
                  <div className="ma-switch-name">{FEATURE_LABEL[f.feature] || f.feature}</div>
                  <div className="ma-choice-d">{FEATURE_DESC[f.feature] || ''}</div>
                  <div className="ma-switch-meta">
                    {f.feature}{f.updated_at ? `・${fmtDateTime(f.updated_at)} に変更` : ''}
                  </div>
                </div>
                <span className="ma-toggle-label" style={{ color: f.enabled ? '#047857' : '#6b7280' }}>
                  {f.enabled ? '開いている' : '閉じている'}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!f.enabled}
                  aria-label={(FEATURE_LABEL[f.feature] || f.feature) + 'を' + (f.enabled ? '閉じる' : '開ける')}
                  className={'ma-toggle' + (f.enabled ? ' ma-toggle--on' : '')}
                  disabled={busy === f.feature}
                  onClick={() => toggle(f)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      <StorageCard s={data.storage} />
    </>
  );
}

// ── 画面 ─────────────────────────────────────────────────────
export default function MessagingAdmin({ user }) {
  const [tab, setTab] = useState('open');
  const [detail, setDetail] = useState(null);
  const [denied, setDenied] = useState('');
  const [listKey, setListKey] = useState(0);

  const onDenied = useCallback((m) => setDenied(m), []);
  const back = (changed) => {
    setDetail(null);
    if (changed) setListKey(k => k + 1);
  };

  return (
    <Page title="メッセージ機能の管理" lead="通報への対応と、機能ごとのスイッチ。運営者（開発者モードの開発者）だけが使えます。" wide>
      <div className="ma-audit" role="note">
        <span aria-hidden="true">●</span>
        ここで見た内容・操作は記録に残ります
      </div>

      {denied ? (
        <div className="card"><Notice kind="error">{denied}</Notice></div>
      ) : (
        <>
          {!detail && (
            <div className="tab-bar" role="tablist">
              {[['open', '通報（未対応）'], ['closed', '通報（対応済み）'], ['features', '機能スイッチ']].map(([k, label]) => (
                <button key={k} type="button" role="tab" aria-selected={tab === k}
                  className={'tab-btn' + (tab === k ? ' active' : '')} onClick={() => setTab(k)}>
                  {label}
                </button>
              ))}
            </div>
          )}
          {detail ? (
            <ReportDetail summary={detail} onBack={back} />
          ) : tab === 'features' ? (
            <FeaturesTab onDenied={onDenied} />
          ) : (
            <ReportList key={tab + ':' + listKey} status={tab} onOpen={setDetail} onDenied={onDenied} />
          )}
        </>
      )}
    </Page>
  );
}
